import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { TenantChip, RoleChip } from "@/components/common/TenantChip";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Copy, Trash2, KeyRound, Check,
} from "lucide-react";
import { SimulatePushSection, PushHistorySection } from "@/components/banker/BankerJourneySections";

export default function AdministrationPage() {
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [bankerKeys, setBankerKeys] = useState([]);
  const [pushHistory, setPushHistory] = useState([]);
  const [freshKeySecret, setFreshKeySecret] = useState(null);
  const [newKeyLabel, setNewKeyLabel] = useState("");

  const loadAll = () => {
    api.get("/admin/tenants").then(r => setTenants(r.data)).catch(() => setTenants([]));
    api.get("/admin/users").then(r => setUsers(r.data)).catch(() => setUsers([]));
    api.get("/admin/audit").then(r => setAudit(r.data)).catch(() => setAudit([]));
    api.get("/admin/banker-keys").then(r => setBankerKeys(r.data)).catch(() => setBankerKeys([]));
    api.get("/admin/banker/push-history").then(r => setPushHistory(r.data)).catch(() => setPushHistory([]));
  };
  useEffect(loadAll, []);

  const createKey = async () => {
    if (!newKeyLabel.trim()) return;
    const { data } = await api.post("/admin/banker-keys", { label: newKeyLabel.trim() });
    setFreshKeySecret(data);
    setNewKeyLabel("");
    api.get("/admin/banker-keys").then(r => setBankerKeys(r.data));
  };

  const revokeKey = async (keyId) => {
    await api.delete(`/admin/banker-keys/${keyId}`);
    api.get("/admin/banker-keys").then(r => setBankerKeys(r.data));
  };

  return (
    <div data-testid={TID.administrationPage} className="space-y-6 animate-fade-in-up">
      <header className="flex items-end justify-between border-b border-border pb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Administration</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-altara-ink mt-1">Tenants, users, roles, bankers &amp; audit</h1>
          <p className="text-sm text-muted-foreground mt-1">Row-level tenant isolation. RBAC enforced on every route.</p>
        </div>
      </header>

      <Tabs defaultValue="tenants">
        <TabsList className="rounded-sm bg-slate-50 border border-slate-200 p-1 h-auto flex-wrap">
          <TabsTrigger value="tenants" className="rounded-sm text-xs">Tenants · {tenants.length}</TabsTrigger>
          <TabsTrigger value="users" className="rounded-sm text-xs">Users · {users.length}</TabsTrigger>
          <TabsTrigger value="roles" className="rounded-sm text-xs">Roles</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-bankers" value="bankers" className="rounded-sm text-xs">Bankers · {bankerKeys.length}</TabsTrigger>
          <TabsTrigger value="audit" className="rounded-sm text-xs">Audit log · {audit.length}</TabsTrigger>
        </TabsList>

        <TabsContent value="tenants" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tenants.map(t => (
              <div key={t.tenant_id} className="border border-slate-200 rounded-sm bg-white p-4">
                <TenantChip name={t.name} color={t.color} />
                <div className="mt-3 text-xs text-slate-600">
                  <div className="capitalize">{t.sector} · {t.country}</div>
                  <div className="font-mono text-[10px] text-slate-500 mt-1">{t.tenant_id}</div>
                </div>
              </div>
            ))}
            {tenants.length === 0 && <div className="text-sm text-muted-foreground">Access to tenants is restricted to platform administrators.</div>}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="border border-slate-200 rounded-sm overflow-hidden bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">User</th>
                  <th className="text-left px-4 py-2 font-medium">Provider</th>
                  <th className="text-left px-4 py-2 font-medium">Roles</th>
                  <th className="text-left px-4 py-2 font-medium">Last login</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} className="border-t border-slate-100">
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-800">{u.name}</div>
                      <div className="text-[10px] text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-2 uppercase text-[10px] tracking-widest">{u.provider}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(u.roles || []).map(r => <RoleChip key={r} role={r} />)}
                      </div>
                    </td>
                    <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Restricted or empty.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[
              "platform_administrator","tenant_administrator","executive","compliance","conduct_risk",
              "customer_experience","call_centre","advisor","learning_administrator","auditor","read_only","api_user",
            ].map(r => (
              <div key={r} className="border border-slate-200 rounded-sm bg-white p-3">
                <RoleChip role={r} />
                <div className="text-[10px] text-slate-500 mt-2 font-mono">Permissions configurable per tenant.</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="border border-slate-200 rounded-sm overflow-hidden bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">When</th>
                  <th className="text-left px-4 py-2 font-medium">Actor</th>
                  <th className="text-left px-4 py-2 font-medium">Action</th>
                  <th className="text-left px-4 py-2 font-medium">Entity</th>
                </tr>
              </thead>
              <tbody>
                {audit.map(a => (
                  <tr key={a.audit_id} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{new Date(a.occurred_at).toLocaleString()}</td>
                    <td className="px-4 py-2">{a.actor_email || "—"}</td>
                    <td className="px-4 py-2 font-mono">{a.action}</td>
                    <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{a.entity_type} · {a.entity_id?.slice(0, 12) || "—"}</td>
                  </tr>
                ))}
                {audit.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No audit entries visible.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ─── Bankers tab ─── */}
        <TabsContent value="bankers" className="mt-4 space-y-6">
          <BankerKeysSection
            keys={bankerKeys}
            newKeyLabel={newKeyLabel}
            setNewKeyLabel={setNewKeyLabel}
            createKey={createKey}
            revokeKey={revokeKey}
            freshKeySecret={freshKeySecret}
            clearFresh={() => setFreshKeySecret(null)}
          />
          <SimulatePushSection onCreated={() => api.get("/admin/banker/push-history").then(r => setPushHistory(r.data))} />
          <PushHistorySection history={pushHistory} onRefresh={() => api.get("/admin/banker/push-history").then(r => setPushHistory(r.data))} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Banker admin sections ────────────────────────────────────

function BankerKeysSection({ keys, newKeyLabel, setNewKeyLabel, createKey, revokeKey, freshKeySecret, clearFresh }) {
  return (
    <section className="border border-slate-200 rounded-sm bg-white p-5">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold flex items-center gap-1.5">
            <KeyRound className="w-3.5 h-3.5" /> Banker API keys
          </div>
          <div className="text-xs text-slate-600 mt-1">
            Each key is bound to one tenant. Bankers use the key as an <code className="text-[11px] bg-slate-50 border border-slate-200 px-1 rounded">Authorization: Bearer</code> to <code className="text-[11px] bg-slate-50 border border-slate-200 px-1 rounded">POST /api/banker/journeys/create</code>.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={newKeyLabel} onChange={e => setNewKeyLabel(e.target.value)}
            placeholder="Label (e.g. Guidewire Production)"
            className="rounded-sm text-xs h-8 w-64"
            data-testid="banker-key-label-input"
          />
          <Button onClick={createKey} disabled={!newKeyLabel.trim()} className="rounded-sm bg-altara-deep hover:bg-altara-ink" data-testid="banker-key-create-btn">
            <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Mint key
          </Button>
        </div>
      </div>

      {freshKeySecret && (
        <div data-testid="banker-key-fresh" className="border border-emerald-200 bg-emerald-50/60 rounded-sm p-4 mb-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5" /> Key minted · save this secret now
          </div>
          <div className="text-xs text-slate-600 mt-1 mb-2">
            This is the <strong>only time</strong> the plaintext secret is shown. Give it to the banker&rsquo;s integration team; we only keep a SHA-256 hash.
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[11px] font-mono bg-white border border-emerald-200 rounded-sm px-3 py-2 select-all overflow-x-auto whitespace-nowrap">
              {freshKeySecret.secret}
            </code>
            <Button size="sm" variant="outline" onClick={() => navigator.clipboard?.writeText(freshKeySecret.secret)} className="rounded-sm">
              <Copy className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" onClick={clearFresh} className="rounded-sm text-slate-500">
              Dismiss
            </Button>
          </div>
          <div className="mt-2 text-[10px] font-mono text-slate-500">
            Label: {freshKeySecret.label} · Key ID: {freshKeySecret.key_id}
          </div>
        </div>
      )}

      <div className="border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Label</th>
              <th className="text-left px-4 py-2 font-medium">Key ID</th>
              <th className="text-left px-4 py-2 font-medium">Created by</th>
              <th className="text-left px-4 py-2 font-medium">Last used</th>
              <th className="text-left px-4 py-2 font-medium">Pushes</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No banker keys minted yet.</td></tr>}
            {keys.map(k => (
              <tr key={k.key_id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-medium text-slate-800">{k.label}</td>
                <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{k.key_id}</td>
                <td className="px-4 py-2 text-slate-600">{k.created_by_email || "—"}</td>
                <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-2 tabular-nums">{k.push_count}</td>
                <td className="px-4 py-2">
                  {k.active
                    ? <span className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded-sm">active</span>
                    : <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-500 border border-slate-200 px-1.5 py-0.5 rounded-sm">revoked</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  {k.active && (
                    <button onClick={() => revokeKey(k.key_id)} className="text-[11px] text-rose-700 hover:underline inline-flex items-center gap-1" data-testid={`banker-key-revoke-${k.key_id}`}>
                      <Trash2 className="w-3 h-3" /> Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

